import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, formatApiError } from "@/lib/api";

function pad(n) {
  return n.toString().padStart(2, "0");
}

export default function QuizPage() {
  const { leadId } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [session, setSession] = useState(null); // {session_id, end_time, questions, answers}
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [now, setNow] = useState(Date.now());
  const saveTimer = useRef(null);

  const submit = useCallback(
    async (autoSubmit = false) => {
      if (!session) return;
      setSubmitting(true);
      try {
        await api.post("/quiz/submit", {
          session_id: session.session_id,
          lead_id: leadId,
          answers,
        });
        if (autoSubmit) {
          toast.message("Time up. Test auto-submitted.");
        } else {
          toast.success("Test submitted successfully.");
        }
        nav(`/submitted/${leadId}`, { replace: true });
      } catch (err) {
        toast.error(formatApiError(err));
        setSubmitting(false);
      }
    },
    [answers, leadId, nav, session],
  );

  // Initial load: try resume, else start
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let data;
        try {
          const res = await api.get(`/quiz/session/${leadId}`);
          data = res.data;
        } catch (err) {
          if (err?.response?.status === 404) {
            const res = await api.post(`/quiz/start`, null, {
              params: { lead_id: leadId },
            });
            data = res.data;
          } else {
            throw err;
          }
        }
        if (cancelled) return;
        if (data.status === "completed") {
          nav(`/submitted/${leadId}`, { replace: true });
          return;
        }
        setSession(data);
        setAnswers(data.answers || {});
        setLoading(false);
      } catch (err) {
        toast.error(formatApiError(err));
        nav(`/register`, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, nav]);

  // Tick clock
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-save answers (debounced)
  useEffect(() => {
    if (!session) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api
        .post("/quiz/save-progress", {
          session_id: session.session_id,
          lead_id: leadId,
          answers,
        })
        .catch(() => {});
    }, 800);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [answers, leadId, session]);

  // Local cache for refresh resilience (server is source of truth)
  useEffect(() => {
    if (!session) return;
    localStorage.setItem(
      `medix_quiz_${leadId}`,
      JSON.stringify({ session_id: session.session_id, answers }),
    );
  }, [answers, leadId, session]);

  const remainingMs = useMemo(() => {
    if (!session) return 0;
    return Math.max(0, new Date(session.end_time).getTime() - now);
  }, [now, session]);

  const totalMs = useMemo(() => {
    if (!session) return 1;
    return new Date(session.end_time).getTime() - new Date(session.start_time).getTime();
  }, [session]);

  // Auto-submit when time expires
  const submittedRef = useRef(false);
  useEffect(() => {
    if (!session) return;
    if (remainingMs === 0 && !submittedRef.current && !submitting) {
      submittedRef.current = true;
      submit(true);
    }
  }, [remainingMs, session, submit, submitting]);

  if (loading || !session) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#0A2540]" />
      </div>
    );
  }

  const minutes = Math.floor(remainingMs / 60000);
  const seconds = Math.floor((remainingMs % 60000) / 1000);
  const lowTime = remainingMs < 2 * 60 * 1000;
  const q = session.questions[currentIdx];
  const total = session.questions.length;
  const progress = ((totalMs - remainingMs) / totalMs) * 100;
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="min-h-screen bg-[#F8F9FA] pb-32 sm:pb-24">
      {/* Sticky timer */}
      <div className="sticky top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto max-w-3xl px-4 py-3 sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div
                className={`flex h-9 w-9 items-center justify-center rounded-md ${
                  lowTime ? "bg-[#991B1B] timer-warning" : "bg-[#0A2540]"
                } text-white`}
              >
                <Clock className="h-4 w-4" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                  Time Remaining
                </div>
                <div
                  className={`font-mono-clock text-lg font-semibold leading-tight ${
                    lowTime ? "text-[#991B1B]" : "text-[#0A2540]"
                  }`}
                  data-testid="quiz-timer"
                >
                  {pad(minutes)}:{pad(seconds)}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Question
              </div>
              <div className="font-display text-base font-semibold text-[#0A2540]">
                {currentIdx + 1} <span className="text-slate-400">/ {total}</span>
              </div>
            </div>
          </div>
          <Progress value={progress} className="mt-3 h-1.5" />
        </div>
      </div>

      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-10">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-8">
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
            {q.subject}
          </div>
          <h2 className="font-display mt-3 text-xl font-semibold leading-snug text-[#0A2540] sm:text-2xl">
            {q.question}
          </h2>

          <div className="mt-6 grid gap-3">
            {q.options.map((opt, idx) => {
              const selected = answers[q.id] === idx;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() =>
                    setAnswers((a) => ({ ...a, [q.id]: idx }))
                  }
                  className={`flex min-h-[60px] w-full items-center gap-4 rounded-md border px-4 py-3 text-left text-sm transition-all sm:text-base ${
                    selected
                      ? "border-2 border-[#0A2540] bg-[#0A2540]/5 text-[#0A2540]"
                      : "border-slate-200 bg-white hover:border-slate-400"
                  }`}
                  data-testid={`quiz-option-${idx}`}
                >
                  <span
                    className={`flex h-7 w-7 flex-none items-center justify-center rounded-full border text-xs font-semibold ${
                      selected
                        ? "border-[#0A2540] bg-[#0A2540] text-white"
                        : "border-slate-300 text-slate-500"
                    }`}
                  >
                    {String.fromCharCode(65 + idx)}
                  </span>
                  <span className="leading-snug">{opt}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Question palette */}
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
          <div className="mb-3 flex items-center justify-between text-xs text-slate-500">
            <span>Answered: {answeredCount} / {total}</span>
            <span className="font-mono-clock">{leadId.slice(0, 8)}</span>
          </div>
          <div className="grid grid-cols-9 gap-1.5 sm:grid-cols-12">
            {session.questions.map((qq, i) => {
              const ans = answers[qq.id] !== undefined;
              const isCur = i === currentIdx;
              return (
                <button
                  key={qq.id}
                  onClick={() => setCurrentIdx(i)}
                  className={`h-8 rounded text-xs font-semibold transition-colors ${
                    isCur
                      ? "bg-[#0A2540] text-white"
                      : ans
                        ? "bg-[#166534]/10 text-[#166534]"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                  data-testid={`quiz-jump-${i}`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
        </div>
      </main>

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3 sm:px-6">
          <Button
            variant="outline"
            disabled={currentIdx === 0}
            onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
            className="h-11"
            data-testid="quiz-prev"
          >
            Previous
          </Button>
          {currentIdx < total - 1 ? (
            <Button
              onClick={() => setCurrentIdx((i) => Math.min(total - 1, i + 1))}
              className="h-11 flex-1 bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
              data-testid="quiz-next"
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={() => submit(false)}
              disabled={submitting}
              className="h-11 flex-1 bg-[#166534] text-white hover:bg-[#14532D]"
              data-testid="quiz-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…
                </>
              ) : (
                "Submit Test"
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
